import { useEffect } from "react"
import { useParams, useOutletContext, useNavigate } from "react-router-dom"
import { useComputed, useSignal, useSignalEffect } from "@preact/signals-react"

import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from "../../components/ConfirmationModal.jsx"
import DebugAccordion from "../../components/DebugAccordion"
import DownloadProgress from "../../components/DownloadProgress.jsx"
import Icon from "../../components/Icon"
import Loadable from "../../components/LoadingAnimation.jsx"
import LoadingAnimation from "../../components/LoadingAnimation.jsx"
import PageTitle from "../../components/PageTitle.jsx"
import TimestampWithUTCTooltip from "../../components/TimestampWithUTCTooltip"
import UploadFormModal from "./components/UploadFormModal"

import { addNotification, NotificationTypes, notifyException } from '../../state/actions/notificationActions.js'
import { fetchDeviceForms, deleteDeviceForm, downloadDeviceForm, updateDeviceFormUploadStatus } from '../../state/actions/deviceFormActions.js'
import { fetchDeviceSchemas } from '../../state/actions/deviceSchemaActions.js'

import { deviceFormsSignal, deviceFormsLoadedSignal, deviceFormsErrorSignal } from '../../state/signals/deviceFormSignals.js'
import { deviceSchemasSignal, deviceSchemasLoadedSignal } from '../../state/signals/deviceSchemaSignals.js'
import { debugSignal } from '../../state/signals/userSettingsSignals.js'

import { ComponentDeviceType, SortDirectionType } from "../../utils/constants"
import { computeSortedData } from '../../utils/table.js'
import { scrollToTop } from '../../utils/utils.js'
import PageSizeSelector from "../../components/PageSizeSelector.jsx";

function Forms() {

	const navigate = useNavigate()
	const { beId } = useParams()
	const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

	const debug = debugSignal.value

	useEffect(() => {
		if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewForms) {
			addNotification('You are not authorized to view UTG Forms for this account.', NotificationTypes.ERROR, true)
			//console.log(userPermissionsSummary.value)
			navigate(`/`)
		}
	}, [userPermissionsSummary.value, navigate])

	useEffect(() => {
		fetchDeviceForms(beId, null, config, accessToken)
		fetchDeviceSchemas(config, accessToken)
	}, [beId, config, accessToken])

	const canEditComputed = useComputed(() => {
		return userPermissionsSummary?.value?.canEditForms
	})

	const selectedFormSignal = useSignal(null)

	const currentPageSignal = useSignal(1)
	const filterStringSignal = useSignal("")

	const rowsPerPageSignal = useSignal(parseInt(window.localStorage.getItem('pagesize')) || 250)
	const selectedRowsSignal = useSignal([])
	const showModalUploadFormSignal = useSignal(false)
	const showModalDeleteFormSignal = useSignal(false)
	const sortColumnSignal = useSignal('')

	const showDownloadProgressSignal = useSignal(false)
	const downloadProgressMessageSignal = useSignal('')

	const sortDirectionSignal = useSignal(SortDirectionType.DESCENDING)

	useSignalEffect(() => {
		window.localStorage.setItem('pagesize', rowsPerPageSignal.value)
	})

	const filteredDataComputed = useComputed(() => {
		try {
			if (!filterStringSignal.value) {
				return deviceFormsSignal.value ? [...deviceFormsSignal.value] : []
			}

			// Convert the filter to lowercase
			const lowerCaseFilter = filterStringSignal.value.toLowerCase()

			// Filter the data based on the filter text
			return deviceFormsSignal.value.filter(item => {
				// Limit search to these fields
				let searchData = {
					name: item.name,
					deviceType: item.deviceType
				}

				return Object.values(searchData).some(value => {
					if (typeof value != 'undefined') {
						return value.toString().toLowerCase().includes(lowerCaseFilter)
					}
				})
			})
		} catch (e) {
			console.error("Forms.filteredData Exception: " + e.toString())
			window.alert("Forms.filteredData Exception: " + e.toString())
			return []
		}
	})

	const sortedDataComputed = useComputed(computeSortedData(filteredDataComputed, sortColumnSignal, sortDirectionSignal))

	const totalPagesComputed = useComputed(() => {
		return Math.ceil(filteredDataComputed.value.length / parseInt(rowsPerPageSignal.value, 10))
	})

	const displayDataComputed = useComputed(() => {
		// Calculate the starting and ending indexes for pagination
		const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
		const endIndex = startIndex + rowsPerPageSignal.value

		return sortedDataComputed.value.slice(startIndex, endIndex)
	})
	const startDisplayRowNumberComputed = useComputed(() => {
		return ((currentPageSignal.value - 1) * rowsPerPageSignal.value) + 1
	})
	const endDisplayRowNumberComputed = useComputed(() => {
		const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
		const endIndex = startIndex + rowsPerPageSignal.value
		return endIndex < filteredDataComputed.value.length ? endIndex : filteredDataComputed.value.length
	})

	//const showUploadFormModal = () => showModalUploadFormSignal.value = true
	const handleCloseModalUploadForm = () => showModalUploadFormSignal.value = false

	const handleFilterStringChange = (e) => {
		currentPageSignal.value = 1
		filterStringSignal.value = e.currentTarget.value
	}

	const handleHeaderTableClick = datafield => {
		// if already sorted on this column then toggle sort direction
		if (datafield == sortColumnSignal.value) {
			sortDirectionSignal.value = sortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
			return
		}

		// update sort column - the computed functions will then run and render the sorted data
		sortColumnSignal.value = datafield
	}

	const accordionData = [
		{ name: 'Properties', data: { Count: deviceFormsSignal.value.length, 'Filter Count': filteredDataComputed.value.length, 'Display Count': displayDataComputed.value.length, Filter: filterStringSignal.value, CurrentPage: currentPageSignal.value, RowsPerPage: rowsPerPageSignal.value, accessToken: accessToken } },
		{ name: 'Filtered Data', data: filteredDataComputed.value },
		{ name: 'Data', data: deviceFormsSignal.value },
		{ name: 'Display Data', data: displayDataComputed.value },
		{ name: 'Selected Rows', data: selectedRowsSignal.value },
		{ name: 'Access Token', data: accessToken },
		{ name: 'App Config', data: config },
		{ name: "Devices loaded", data: deviceFormsLoadedSignal.value ? "Yes" : "No" }
	]

	const handlePageSizeSelect = (v) => {
		currentPageSignal.value = 1
		rowsPerPageSignal.value = parseInt(v, 10)
	}

	const invertedDeviceTypesComputed = useComputed(() => {
		return Object.keys(ComponentDeviceType).reduce((accumulator, key) => {
			accumulator[ComponentDeviceType[key]] = key.toLowerCase()
			return accumulator;
		}, {})
	})

	const humanReadableDeviceTypesComputed = useComputed(() => {
		if (deviceSchemasSignal.value.length == 0) {
			return []
		}
		const lowerCaseDeviceTypes = {}
		const keys = Object.keys(ComponentDeviceType)
		for (let i = 0; i < keys.length; i++) {
			const key = keys[i]
			const lowerKey = key.toLowerCase()
			lowerCaseDeviceTypes[lowerKey] = ComponentDeviceType[key]
		}
		let deviceTypesToNames = []
		const schemas = deviceSchemasSignal.value
		for (let i = 0; i < schemas.length; i++) {
			const schema = schemas[i]
			deviceTypesToNames[lowerCaseDeviceTypes[schema.key]] = schema.name
		}
		//console.log("Returning the following:")
		//console.log(deviceTypesToNames)
		return deviceTypesToNames
	})

	function PaginationItems() {
		let items = []
		for (let i = 1; i <= totalPagesComputed.value; i++) {
			items.push(
				<Pagination.Item key={i} active={i === currentPageSignal.value} onClick={() => currentPageSignal.value = i}>{i}</Pagination.Item>
			)
		}
		return items
	}

	function TableColumnHeader({ title, sortable, datafield }) {
		// non-sortable columns do not have click handlers
		if (!sortable) {
			return (
				<th>{title}</th>
			)
		}

		// sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
		if (datafield != sortColumnSignal.value) {
			return (
				<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
			)
		}

		// datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
		if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
			return (
				<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
					<Icon icon="caret-down" width="16" height="16"></Icon>
				</th>
			)
		}

		// else must be ascending
		return (
			<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
				<Icon icon="caret-up" width="16" height="16"></Icon>
			</th>
		)
	}

	const showDeleteModal = function (item) {
		selectedFormSignal.value = item
		showModalDeleteFormSignal.value = true
	}

	const handleCloseModalDeleteForm = function () {
		selectedFormSignal.value = null
		showModalDeleteFormSignal.value = false
	}

	const downloadForm = async function (item) {
		downloadProgressMessageSignal.value = 'Downloading...'
		showDownloadProgressSignal.value = true
		downloadDeviceForm(beId, item, config, accessToken).catch(
			(x) => {
				notifyException(x, "Error downloading form")
			}
		).finally(
			() => {
				showDownloadProgressSignal.value = false
			}
		)
	}

	const deleteFormMessageComputed = useComputed(() => {
		if (selectedFormSignal.value != null) {
			return `Are you sure you want to delete this form: ${selectedFormSignal.value.name}`
		}
		return null
	})

	const deleteForm = async function (record) {
		if (record) {
			downloadProgressMessageSignal.value = "Deleting form..."
			showDownloadProgressSignal.value = true
			deleteDeviceForm(beId, record, config, accessToken).then(
				() => {
					addNotification(`Form ${record.name} was deleted successfully.`, NotificationTypes.SUCCESS, false, 'Form deleted')
				}
			).catch(
				(exception) => {
					addNotification(`Deletion of form ${record.name} failed: `.concat(exception.toString()), NotificationTypes.ERROR, false, 'Form deletion failed')
				}
			).finally(
				() => {
					showDownloadProgressSignal.value = false
				}
			)
			handleCloseModalDeleteForm()
		}
	}

	function TableRow({ item }) {
		if (!Object.prototype.hasOwnProperty.call(item, "humanReadableDeviceType")) {
			const deviceNames = humanReadableDeviceTypesComputed.value
			const length = deviceNames.length
			//console.log("There are ".concat(length).concat(" device types"))
			//console.log(deviceNames)
			if (item.deviceType >= length) {
				item.humanReadableDeviceType = "Unknown device type ".concat(item.deviceType)
			}
			else {
				item.humanReadableDeviceType = deviceNames[item.deviceType]
			}
		}
		return (
			<tr>
				<td>
					<Dropdown>
						<Dropdown.Toggle variant="link" size="sm" id="actions"><Icon icon="gear" width="16" height="16"></Icon></Dropdown.Toggle>
						<Dropdown.Menu>
							<Dropdown.Item onClick={() => downloadForm(item)}><Icon icon="download" width="16" height="16"></Icon>&nbsp;Download form</Dropdown.Item>
							{canEditComputed.value && <Dropdown.Item onClick={() => showDeleteModal(item)}>
								<span style={{ color: "#f00" }}>
									<Icon icon="trash" width="16" height="16"></Icon>
								</span>
								&nbsp;Delete form
							</Dropdown.Item>}
						</Dropdown.Menu>
					</Dropdown>
				</td>
				<td>{item.name}</td>
				<td data-devicecode={item.deviceType}>{item.humanReadableDeviceType}</td>
				<td><TimestampWithUTCTooltip>{item.lastUpdatedAt}</TimestampWithUTCTooltip></td>
			</tr>
		)
	}

	function TableRows({ items }) {
		if (!deviceFormsLoadedSignal.value) {
			return <tr><td colSpan={4}><LoadingAnimation /></td></tr>
		}
		if (items && items.length) {
			const rows = items.map(item => {
				return <TableRow item={item} key={`${item.id}`} />
			})
			return rows
		}
		return <tr><td colSpan={4} className="text-center">No device forms found</td></tr>
	}

	return (
		<>
			<DownloadProgress showSignal={showDownloadProgressSignal} messageSignal={downloadProgressMessageSignal} />
			<ConfirmationModal
				show={showModalDeleteFormSignal.value}
				handleCancel={handleCloseModalDeleteForm}
				handleConfirm={
					() => {
						showModalDeleteFormSignal.value = false
						deleteForm(selectedFormSignal.value)
					}
				}
				message={deleteFormMessageComputed.value}
				title={"Delete Form"}
			/>
			<PageTitle title='Form Manager' canEditSignal={canEditComputed} />
			<Nav className="justify-content-between px-3 py-1 my-2 bg-light">
				<Nav.Item className="me-auto">
					<PageSizeSelector pageSizeSignal={rowsPerPageSignal} handlePageSizeSelect={handlePageSizeSelect} />
				</Nav.Item>
				<Nav.Item>
					<Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" onChange={handleFilterStringChange} />
				</Nav.Item>
				{canEditComputed.value && <Nav.Item className="ms-2">
					<Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={() => showModalUploadFormSignal.value = true}>
						<span className="me-1">
							<Icon icon='upload' width="16" height="16" ></Icon>
						</span>
						Upload
					</Button>
				</Nav.Item>}
			</Nav>
			<Table responsive striped hover size="sm" className="cc-table">
				<thead>
					<tr>
						<TableColumnHeader title={'Action'} sortable={false}></TableColumnHeader>
						<TableColumnHeader title={'Name'} sortable={true} datafield={'name'}></TableColumnHeader>
						<TableColumnHeader title={'Device Type'} sortable={true} datafield={'humanReadableDeviceType'}></TableColumnHeader>
						<TableColumnHeader title={'Upload Date'} sortable={true} datafield={'lastUpdatedAt'}></TableColumnHeader>
					</tr>
				</thead>
				<tbody><TableRows items={displayDataComputed.value} /></tbody>
				<tfoot>
					<tr>
						<td colSpan={19}>{
							filteredDataComputed.value.length > 0 && (
								<>Displaying {startDisplayRowNumberComputed.value}-{endDisplayRowNumberComputed.value} of {filteredDataComputed.value.length}</>
							)
						}</td>
					</tr>
				</tfoot>
			</Table>
			<Pagination><PaginationItems /></Pagination>
			{
				debugSignal.value && (
					<DebugAccordion data={accordionData} debug={debug} />
				)
			}
			<UploadFormModal
				show={showModalUploadFormSignal.value}
				handleCancel={handleCloseModalUploadForm}
				deviceTypes={deviceSchemasSignal.value}
			></UploadFormModal>
		</>
	)
}

export default Forms